import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { GeoFilterDefaultModal, GeoFilterModal } from '@feathr/extender/components';

import { useGeoFiltersPage } from './GeoFiltersPage.hooks';
import { convertTargetableToFormData } from './GeoFiltersPage.utils';
import GeoFiltersTable from './GeoFiltersTable';

import * as styles from './GeoFiltersPage.css';

function GeoFiltersPage(): JSX.Element {
  const { t } = useTranslation();

  const {
    selectedGeoSet,
    geoSetForDefault,
    defaultGeoSetId,
    isSavingDefault,
    modals,
    handleCloseModal,
    handleSaveGeoSet,
    handleOpenCreate,
    handleOpenEdit,
    handleSetDefault,
    handleConfirmSetDefault,
  } = useGeoFiltersPage();

  return (
    <Page
      actions={[
        <Button
          key={'create-set'}
          onClick={handleOpenCreate}
          prefix={<Icon icon={faPlus} />}
          type={'primary'}
        >
          {t('Create set')}
        </Button>,
      ]}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      contentClassName={styles.pageContent}
      description={t(
        'Create and manage geofilter sets that allow you to target your campaigns by location. These sets can be used across ad campaigns.',
      )}
      showHeader={true}
      title={'Geofilters'}
    >
      <GeoFilterModal
        context={modals.editCreate.context}
        initialData={
          selectedGeoSet ? convertTargetableToFormData(selectedGeoSet, defaultGeoSetId) : undefined
        }
        key={selectedGeoSet?.id || 'create'}
        onClose={handleCloseModal}
        onSaveChanges={handleSaveGeoSet}
        opened={modals.editCreate.isOpen}
      />
      <GeoFilterDefaultModal
        geoFilterName={geoSetForDefault?.get('name') ?? ''}
        isLoading={isSavingDefault}
        onClose={modals.defaultGeoSet.close}
        onConfirm={handleConfirmSetDefault}
        opened={modals.defaultGeoSet.isOpen}
      />
      <GeoFiltersTable
        defaultGeoSetId={defaultGeoSetId}
        onCreateSet={handleOpenCreate}
        onEditSet={handleOpenEdit}
        onSetDefault={handleSetDefault}
      />
    </Page>
  );
}

export default observer(GeoFiltersPage);
